import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, CircularProgress, Container, FormControlLabel, Icon, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { CreateHeaders, textHeader } from '@frontend-workspace/react-shared-lib';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from '../page-title/page-title';
import { makeRequest } from '../requestUtils';
import { AccountReconciliationResult, SalesForceAccountReconciliation } from './sales-force-account-reconciliation';
import { AssetReconciliationResult, SalesForceAssetReconciliation } from './sales-force-asset-reconciliation';
import WebkuaTable from '../webkua-table/webkua-table';
import { ContractReconciliationResult, SalesForceContractReconciliation } from './sales-force-contract-reconciliation';
import { SalesforceInternalValidation } from './salesforce-internal-tab';

interface SalesforceObjectMetadata {
  objectName: string;
  fields: Array<{
    name: string;
    label: string | null;
    type: string;
    referenceTo: string[];
    relationshipName: string | null;
  }>;
}

function useComparison<T>(url: string) {
  const [result, setResult] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inFlight = useRef<Promise<T> | null>(null);

  const load = () => {
    if (inFlight.current) return;
    setResult(null);
    setHasError(false);
    setIsLoading(true);
    const request: Promise<T> = makeRequest(url);
    inFlight.current = request;
    request.then(setResult).catch(() => setHasError(true)).finally(() => {
      if (inFlight.current === request) {
        inFlight.current = null;
        setIsLoading(false);
      }
    });
  };

  const reset = () => {
    setResult(null);
    setHasError(false);
  };

  return { result, isLoading, hasError, load, reset };
}

type Entity = 'accounts' | 'assets' | 'contracts';

export function SalesForceAccountReconciliationPage() {
  const { t } = useTranslation();
  const accounts = useComparison<AccountReconciliationResult>('salesforce/reconciliation/accounts');
  const assets = useComparison<AssetReconciliationResult>('salesforce/reconciliation/assets');
  const contracts = useComparison<ContractReconciliationResult>('salesforce/reconciliation/contracts');
  const [selected, setSelected] = useState<Record<Entity, boolean>>({ accounts: false, assets: false, contracts: false });
  const [fetched, setFetched] = useState<Entity[]>([]);
  const [activeTab, setActiveTab] = useState<Entity | 'internal'>('accounts');
  const [metadataObject, setMetadataObject] = useState('Account');
  const metadata = useComparison<SalesforceObjectMetadata>(`salesforce/reconciliation/metadata/${metadataObject}`);
  const internalAvailable = fetched.length === 3 && accounts.result != null && assets.result != null
    && contracts.result != null && !accounts.isLoading && !assets.isLoading && !contracts.isLoading
    && !accounts.hasError && !assets.hasError && !contracts.hasError;

  const loadSelected = () => {
    if (accounts.isLoading || assets.isLoading || contracts.isLoading) return;
    const entities = (['accounts', 'assets', 'contracts'] as Entity[]).filter((entity) => selected[entity]);
    if (!entities.length) return;
    setFetched(entities);
    setActiveTab(entities[0]);
    if (selected.accounts) accounts.load();
    if (selected.assets) assets.load();
    if (selected.contracts) contracts.load();
  };

  return (
    <Container className="container-fluid pt-4 px-0" maxWidth={false}>
      <PageTitle title={t('app.accountReconciliation')} subtitle="" />
      <FormControlLabel
        control={<Checkbox checked={selected.accounts} onChange={(event) =>
          setSelected((current) => ({ ...current, accounts: event.target.checked }))} />}
        label={t('salesForceAccountReconciliation.accounts')}
      />
      <FormControlLabel
        control={<Checkbox checked={selected.assets} onChange={(event) =>
          setSelected((current) => ({ ...current, assets: event.target.checked }))} />}
        label={t('salesForceAccountReconciliation.assets')}
      />
      <FormControlLabel
        control={<Checkbox checked={selected.contracts} onChange={(event) =>
          setSelected((current) => ({ ...current, contracts: event.target.checked }))} />}
        label={t('salesForceAccountReconciliation.contracts')}
      />
      <Button onClick={loadSelected} disabled={(!selected.accounts && !selected.assets && !selected.contracts) ||
        accounts.isLoading || assets.isLoading || contracts.isLoading}>
        {t('salesForceAccountReconciliation.fetch')}
      </Button>
      {fetched.length > 0 && (
        <>
          <Tabs value={activeTab} onChange={(_, entity: Entity | 'internal') => setActiveTab(entity)}
            aria-label={String(t('salesForceAccountReconciliation.results'))}>
            {fetched.map((entity) => (
              <Tab key={entity} value={entity} label={t(`salesForceAccountReconciliation.${entity}`)} />
            ))}
            <Tab value="internal" disabled={!internalAvailable} label={t('salesforceInternal.tab')}
              title={String(t('salesforceInternal.requiresAll'))} />
          </Tabs>
          <Box role="tabpanel" hidden={activeTab !== 'internal'}>
            {activeTab === 'internal' && (internalAvailable && assets.result && contracts.result
              ? <SalesforceInternalValidation assets={assets.result} contracts={contracts.result} />
              : <Alert severity="info">{t('salesforceInternal.requiresAll')}</Alert>)}
          </Box>
          {fetched.map((entity) => {
            const comparison = entity === 'accounts' ? accounts : entity === 'assets' ? assets : contracts;
            return (
              <Box key={entity} role="tabpanel" hidden={activeTab !== entity}>
                {comparison.isLoading && (
                  <Box role="status"><CircularProgress size={24} /> {t('salesForceAccountReconciliation.loading')}</Box>
                )}
                {comparison.hasError && (
                  <Alert severity="error">
                    {t(entity === 'accounts'
                      ? 'salesForceAccountReconciliation.loadError'
                      : entity === 'assets' ? 'salesForceAccountReconciliation.assetLoadError'
                        : 'salesForceAccountReconciliation.contractLoadError')}
                  </Alert>
                )}
                {entity === 'accounts' && accounts.result && <SalesForceAccountReconciliation
                  result={accounts.result} onRefresh={accounts.load} />}
                {entity === 'assets' && assets.result && <SalesForceAssetReconciliation
                  result={assets.result} onRefresh={assets.load} />}
                {entity === 'contracts' && contracts.result && <SalesForceContractReconciliation
                  result={contracts.result} onRefresh={contracts.load} />}
              </Box>
            );
          })}
        </>
      )}
      <Accordion sx={{ mt: 2 }} disableGutters elevation={0} TransitionProps={{ unmountOnExit: true }}>
        <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
          <Typography variant="body2">{t('salesForceMetadata.title')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField select SelectProps={{ native: true }} size="small"
                label={t('salesForceMetadata.object')} value={metadataObject} disabled={metadata.isLoading}
                onChange={(event) => { metadata.reset(); setMetadataObject(event.target.value); }}>
                {['Account', 'Asset', 'Contract'].map((object) => <option key={object} value={object}>{object}</option>)}
              </TextField>
              <Button onClick={metadata.load} disabled={metadata.isLoading}>{t('salesForceMetadata.fetch')}</Button>
            </Stack>
            {metadata.isLoading && <Box role="status"><CircularProgress size={24} /> {t('salesForceMetadata.loading')}</Box>}
            {metadata.hasError && <Alert severity="error">{t('salesForceMetadata.error')}</Alert>}
            {metadata.result && <Box data-testid="salesforce-metadata-results">
              <Typography variant="caption">{t('salesForceMetadata.loaded', {
                objectName: metadata.result.objectName, count: metadata.result.fields.length,
              })}</Typography>
              <WebkuaTable
                data={metadata.result.fields.map((field) => ({ ...field, referenceTo: field.referenceTo.join(', ') }))}
                headers={CreateHeaders(['name', 'label', 'type', 'referenceTo', 'relationshipName']
                  .map((field) => textHeader(field, t(`salesForceMetadata.${field}`))))
                  .map((header) => ({ ...header, filter: 'agTextColumnFilter' }))}
              />
            </Box>}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
}
